import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, Download, Shield } from "lucide-react";

export const TherapyDataManagement = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isClearingMemory, setIsClearingMemory] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleClearMemoryBank = async () => {
    setIsClearingMemory(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call the database function to clear memory bank
      const { error } = await supabase.rpc('clear_user_memory_bank', {
        p_user_id: user.id
      });

      if (error) throw error;

      const dontShowAgain = localStorage.getItem('hideMemoryClearNotification') === 'true';
      
      if (!dontShowAgain) {
        toast.success("Memory bank cleared. Therappi will start fresh in new conversations.", {
          action: {
            label: "Don't show again",
            onClick: () => {
              localStorage.setItem('hideMemoryClearNotification', 'true');
              toast.success("You won't see this notification again");
            },
          },
        });
      }
    } catch (error) {
      console.error('Error clearing memory bank:', error);
      toast.error("Failed to clear memory bank");
    } finally {
      setIsClearingMemory(false);
    }
  };

  const handleDeleteAllMemory = async () => {
    setIsDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete all conversations (cascades to messages and summaries)
      const { error } = await supabase
        .from('therapy_conversations')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      const dontShowAgain = localStorage.getItem('hideDataDeleteNotification') === 'true';
      
      if (!dontShowAgain) {
        toast.success("All reflection conversations have been permanently deleted", {
          action: {
            label: "Don't show again",
            onClick: () => {
              localStorage.setItem('hideDataDeleteNotification', 'true');
              toast.success("You won't see this notification again");
            },
          },
        });
      }
    } catch (error) {
      console.error('Error deleting memory:', error);
      toast.error("Failed to delete conversations");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get all conversations
      const { data: conversations, error: convError } = await supabase
        .from('therapy_conversations')
        .select('*')
        .eq('user_id', user.id);

      if (convError) throw convError;

      // Get all messages
      const { data: messages, error: msgError } = await supabase
        .from('therapy_messages')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (msgError) throw msgError;

      // Get all summaries
      const { data: summaries, error: sumError } = await supabase
        .from('therapy_memory_summaries')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (sumError) throw sumError;

      const exportData = {
        exported_at: new Date().toISOString(),
        conversations,
        messages,
        memory_summaries: summaries
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `therappi-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Your reflection data has been exported");
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Therappi® Data Management
          </CardTitle>
          <CardDescription>
            Manage your Therappi® conversations and memory. Your privacy and data are important to us.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold">Data Retention Policy</h3>
            <p className="text-sm text-muted-foreground">
              Your Therappi® conversations are stored securely as long as you have an active account. 
              If your Therappi® subscription ends, your data will be retained for 90 days before scheduled deletion. 
              You will receive notifications before any data deletion occurs.
            </p>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleExportData}
              disabled={isExporting}
              className="w-full justify-start"
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? "Exporting..." : "Export All Therappi® Data"}
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  disabled={isClearingMemory}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Memory Bank Only
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Memory Bank?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete all memory summaries that Therappi® has created from your conversations. 
                    Your actual conversation history will remain intact, but Therappi® will no longer remember 
                    insights from deleted conversations in new sessions. This is useful if you want a fresh 
                    start while keeping your conversation records.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleClearMemoryBank}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Clear Memory Bank
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full justify-start"
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Therappi® Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all your Therappi® conversations, messages, and memory summaries. 
                    Therappi® will not remember anything from your previous sessions. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAllMemory}
                    className="bg-[hsl(183,92%,51%)] text-white hover:bg-[hsl(183,92%,41%)]"
                  >
                    Delete Everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How Therappi® Remembers You</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Therappi® uses an advanced memory system to provide truly personalised support:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li>
              <strong>Recent Memory:</strong> The last 50 messages are kept in full detail for immediate context
            </li>
            <li>
              <strong>Long-term Memory:</strong> Every 50 messages, Therappi® automatically creates a summary 
              capturing key themes, patterns, progress, and your unique way of communicating
            </li>
            <li>
              <strong>Unlimited History:</strong> All summaries are retained indefinitely (while your account is active), 
              so Therappi® can remember everything from your very first conversation
            </li>
            <li>
              <strong>Deep Understanding:</strong> This hierarchical memory allows Therappi® to know you better over time, 
              building a genuine understanding of your journey
            </li>
          </ul>
          <p className="pt-2">
            With up to 300 messages per month (and the ability to purchase more), you can have extensive 
            conversations while Therappi® maintains deep, personalised memory of your entire journey.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
